// MyAccountTab.js — личный кабинет пользователя.
// Доступен ВСЕМ ролям (owner, admin, accountant, viewer) — каждый может управлять
// своими данными и безопасностью независимо от роли в организации.
//
// Содержит:
//   1. PersonalDataCard — имя/email/телефон + смена своего пароля
//   2. TwoFactorCard — 2FA (импорт из AdminTab.js, не дублируем)
//   3. SessionsCard — мои устройства (импорт)
//   4. SecurityEventsCard — активность безопасности (импорт)
//
// Stage 11/Этап 2: эта вкладка решает проблему — раньше sub-users не имели UI
// для управления своим аккаунтом (всё было в AdminTab, доступном только owner-у).

import React, { useState, useEffect } from "react";
import {
  User, Mail, Phone, Lock, Edit3, X, AlertCircle, CheckCircle, UserCheck,
} from "lucide-react";
import { useTheme, ROLE_LABELS, Card, Btn, PageHeader, stripCyrillic } from "./shared";
import { S, getLang } from "./strings";
import { phoneDigits, formatPhone, isValidPhone } from './phone';
import * as api from "./api";
// Stage 11/Этап 2: переиспользуем security-карточки из AdminTab (named exports).
// Не дублируем код — у этих компонентов сложная логика (2FA wizard, сессии, события).
import { TwoFactorCard, SessionsCard, SecurityEventsCard } from "./AdminTab";

export default function MyAccountTab({ user, globalUser, setGlobalUser }) {
  const t = useTheme();

  return (
    <div>
      <PageHeader
        title={S().admin.myAccount}
        sub={S().admin.myAccountSubtitle}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* ─── Личные данные + смена пароля ─── */}
        <PersonalDataCard user={user} globalUser={globalUser} setGlobalUser={setGlobalUser} />

        {/* ─── Заголовок секции "Безопасность" ─── */}
        <div style={{ fontSize: 13, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 6 }}>
          {S().admin.securitySection}
        </div>

        {/* ─── 2FA + Мои устройства + События — переиспользуем из AdminTab ─── */}
        <TwoFactorCard />
        <SessionsCard />
        <SecurityEventsCard />
      </div>
    </div>
  );
}

// PersonalDataCard — личные данные текущего юзера.
// Email меняется через 3-step OTP (api.requestEmailChange / api.confirmEmailChange).
// Телефон — через api.updateProfile.
// Пароль — через api.changePassword (требует currentPassword, ротирует все сессии).
//
// Этот компонент по логике повторяет блок "Мой аккаунт" из AdminTab.AProfile,
// но независимый — со своим state. AProfile-блок останется только для owner
// в "Профиль компании", а MyAccountTab — для всех ролей.
function PersonalDataCard({ user, globalUser, setGlobalUser }) {
  const t = useTheme();
  // Этап 2 v2: email доступен всем ролям. Sub-users могут указать свой email
  // и использовать self-recovery через "Забыли пароль". Owner также видит и
  // может менять email сотрудника через AUsers — на случай увольнения, чтобы
  // старый сотрудник не сделал forgot-password на корпоративный email.

  // ── Смена email (3-шаговый OTP flow с currentPassword) ──
  // Шаги: 0 = idle, 1 = ввод нового email + текущий пароль, 2 = ввод OTP кода
  const [mailStep, setMailStep] = useState(0);
  const [newMail, setNewMail] = useState('');
  const [mailOtp, setMailOtp] = useState('');
  const [mailLoading, setMailLoading] = useState(false);
  const [mailError, setMailError] = useState('');
  const [mailSuccess, setMailSuccess] = useState('');
  const [mailCurrentPw, setMailCurrentPw] = useState('');

  const mailChangeCancel = () => {
    setMailStep(0);
    setNewMail('');
    setMailOtp('');
    setMailError('');
    setMailCurrentPw(''); // Stage 11: пароль не оставляем в state после отмены
  };

  const mailRequestCode = async () => {
    setMailError('');
    const trimmed = newMail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setMailError(S().emailChange.newEmailPh || 'Введите корректный email');
      return;
    }
    if (globalUser && trimmed === (globalUser.email || '').toLowerCase()) {
      setMailError(S().emailChange.sameEmail || 'Новый email совпадает с текущим');
      return;
    }
    if (!mailCurrentPw) {
      setMailError(S().admin.currentPasswordRequired || 'Введите текущий пароль');
      return;
    }
    setMailLoading(true);
    try {
      await api.requestEmailChange(mailCurrentPw, trimmed, getLang());
      setMailCurrentPw('');
      setMailStep(2);
    } catch (e) {
      const msg = e && e.message ? e.message : '';
      const m = msg.match(/(\d+)\s*сек/);
      if (m && S().emailChange.rateLimit) {
        setMailError(S().emailChange.rateLimit(m[1]));
      } else {
        setMailError(msg || (S().emailChange.sendError || 'Не удалось отправить код'));
      }
    } finally {
      setMailLoading(false);
    }
  };

  const mailConfirm = async () => {
    setMailError('');
    const code = mailOtp.trim();
    if (code.length !== 6) {
      setMailError(S().emailChange.invalidCode || 'Неверный код');
      return;
    }
    setMailLoading(true);
    try {
      const res = await api.confirmEmailChange(code, getLang());
      if (res && res.user) {
        if (setGlobalUser) setGlobalUser(prev => ({ ...(prev || {}), ...res.user }));
        setMailSuccess(S().emailChange.successMsg || 'Email успешно изменён');
        setMailStep(0);
        setNewMail('');
        setMailOtp('');
        setTimeout(() => setMailSuccess(''), 4000);
      } else {
        setMailError(S().emailChange.invalidCode || 'Неверный код');
      }
    } catch (e) {
      const msg = (e && e.message) || '';
      if (/expired|истёк|истек/i.test(msg)) {
        setMailError(S().emailChange.expired || 'Код истёк, запросите новый');
      } else {
        setMailError(msg || (S().emailChange.invalidCode || 'Неверный код'));
      }
    } finally {
      setMailLoading(false);
    }
  };

  // ── Смена телефона (api.updateProfile) ──
  const [phoneEdit, setPhoneEdit] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(phoneDigits(globalUser?.phone || ''));
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneErr, setPhoneErr] = useState('');
  const [phoneOk, setPhoneOk] = useState('');

  useEffect(() => {
    setPhoneDraft(phoneDigits(globalUser?.phone || ''));
  }, [globalUser?.phone]);

  const phoneSave = async () => {
    setPhoneErr('');
    const digits = phoneDigits(phoneDraft);
    if (!isValidPhone(digits)) {
      setPhoneErr(S().admin.phoneMinDigits || 'Минимум 7 цифр');
      return;
    }
    setPhoneLoading(true);
    try {
      const updated = await api.updateProfile({ phone: digits });
      if (setGlobalUser) setGlobalUser(prev => ({ ...(prev || {}), ...updated }));
      setPhoneOk(S().admin.phoneSaved || 'Телефон сохранён');
      setPhoneEdit(false);
      setTimeout(() => setPhoneOk(''), 3000);
    } catch (e) {
      setPhoneErr((e && e.message) || (S().admin.phoneSaveErr || 'Не удалось сохранить'));
    } finally {
      setPhoneLoading(false);
    }
  };

  const phoneCancel = () => {
    setPhoneEdit(false);
    setPhoneDraft(phoneDigits(globalUser?.phone || ''));
    setPhoneErr('');
  };

  // ── Смена пароля (api.changePassword — Stage 11 с full session rotation) ──
  const [pw, setPw] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const savePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (pw.length < 8) {
      setPwError(S().setup.passwordMinLength || 'Минимум 8 символов');
      return;
    }
    if (!currentPw) {
      setPwError(S().admin.currentPasswordRequired || 'Введите текущий пароль');
      return;
    }
    setPwSaving(true);
    try {
      const res = await api.changePassword(currentPw, pw);
      // Stage 11: api.changePassword возвращает {ok, revokedSessions}.
      // revokedSessions — это сколько ДРУГИХ устройств было выкинуто (текущая остаётся живой).
      const others = res?.revokedSessions || 0;
      setPwSuccess(others > 0
        ? `Пароль изменён. Завершено других сессий: ${others}.`
        : 'Пароль изменён.');
      setPw('');
      setCurrentPw('');
      setTimeout(() => setPwSuccess(''), 5000);
    } catch (err) {
      setPwError((err && err.message) || 'Не удалось сменить пароль');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserCheck size={18} color={t.accentPurple} /> {S().admin.personalDataSection}
      </div>

      {/* Аватар + имя + роль */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: t.bgInput, borderRadius: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: t.accentPurple, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800 }}>
          {(user?.name || globalUser?.name || 'U').charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{user?.name || globalUser?.name || ''}</div>
          {user?.role && (
            <div style={{ fontSize: 13, color: t.textSec }}>
              {S().admin.roleLabel} <span style={{ color: t.accentPurple, fontWeight: 700 }}>{ROLE_LABELS[user.role] || user.role}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Email ── для всех ролей (sub-user может self-recovery через forgot-password) ── */}
      {globalUser && (
        <div>
          <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>
            {S().emailChange.title || 'Смена email'}
          </label>

          {mailStep === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Mail size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
                <input readOnly value={globalUser.email || ''} style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, opacity: 0.8 }} />
              </div>
              <Btn variant="ghost" onClick={() => { setMailStep(1); setMailError(''); setMailSuccess(''); }}>
                <Edit3 size={14} /> {S().emailChange.changeBtn || 'Изменить'}
              </Btn>
            </div>
          )}

          {mailStep === 1 && (
            /* Чанк F.1 (2026-05-15): обёрнут в <form> для убирания Chrome DOM warning
               "Password field is not contained in a form". onSubmit preventDefault,
               кнопки остаются onClick-based — поведение не меняется. */
            <form onSubmit={e => e.preventDefault()} className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
              {/* Чанк F.2 (2026-05-15): hidden username для accessibility (Chrome DOM warning). */}
              <input type="text" name="username" autoComplete="username" defaultValue={globalUser?.login || globalUser?.email || user?.login || ""} readOnly style={{ display: "none" }} aria-hidden="true" />
              <div style={{ fontSize: 12, color: t.textMuted }}>
                {S().emailChange.step1Desc || 'Введите новый email. Мы отправим на него код подтверждения.'}
              </div>
              <input
                type="email"
                autoComplete="email"
                placeholder={S().emailChange.newEmailPh || 'new@example.com'}
                value={newMail}
                onChange={e => setNewMail(e.target.value)}
                style={{ width: '100%', fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38 }}
              />
              <input
                type="password"
                autoComplete="current-password"
                placeholder={S().admin.currentPasswordPh || 'Текущий пароль'}
                value={mailCurrentPw}
                onChange={e => { setMailCurrentPw(e.target.value); setMailError(''); }}
                style={{ width: '100%', fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="success" onClick={mailRequestCode} disabled={mailLoading || !mailCurrentPw} style={{ flex: 1 }}>
                  {mailLoading ? '...' : (S().emailChange.sendCode || 'Отправить код')}
                </Btn>
                <Btn variant="ghost" onClick={mailChangeCancel}><X size={16} /></Btn>
              </div>
            </form>
          )}

          {mailStep === 2 && (
            <div className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
              <div style={{ fontSize: 12, color: t.textMuted }}>
                {S().emailChange.step2Desc || 'Мы отправили 6-значный код на новый адрес. Введите его ниже.'} <span style={{ color: t.text, fontWeight: 600 }}>{newMail}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={S().emailChange.codePh || '123456'}
                  value={mailOtp}
                  onChange={e => setMailOtp(e.target.value.replace(/\D/g, ''))}
                  style={{ flex: 1, fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38, letterSpacing: 2 }}
                />
                <Btn variant="success" onClick={mailConfirm} disabled={mailLoading || mailOtp.length !== 6}>
                  {mailLoading ? '...' : (S().emailChange.confirm || 'Подтвердить')}
                </Btn>
                <Btn variant="ghost" onClick={mailChangeCancel}><X size={16} /></Btn>
              </div>
            </div>
          )}

          {mailError && (
            <div style={{ fontSize: 12, color: t.accentRed, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={12} /> {mailError}
            </div>
          )}
          {mailSuccess && (
            <div style={{ fontSize: 12, color: t.accentGreen, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={12} /> {mailSuccess}
            </div>
          )}
        </div>
      )}

      {/* ── Телефон ── */}
      {globalUser && (
        <div>
          <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>
            {S().admin.myPhoneLabel || 'Мой телефон'}
          </label>

          {!phoneEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Phone size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
                <input
                  readOnly
                  value={globalUser.phone ? formatPhone(globalUser.phone) : (S().admin.phoneNotSet || 'Не указан')}
                  style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: globalUser.phone ? t.text : t.textMuted, opacity: 0.8 }}
                />
              </div>
              <Btn variant="ghost" onClick={() => { setPhoneEdit(true); setPhoneErr(''); setPhoneOk(''); setPhoneDraft(phoneDigits(globalUser?.phone || '')); }}>
                <Edit3 size={14} /> {S().emailChange.changeBtn || 'Изменить'}
              </Btn>
            </div>
          )}

          {phoneEdit && (
            <div className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
              <div style={{ fontSize: 12, color: t.textMuted }}>
                {S().admin.phoneHint || 'Только цифры, минимум 7. Пробелы система расставит автоматически.'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  inputMode="tel"
                  placeholder={S().admin.phonePlaceholder}
                  value={phoneDraft}
                  onChange={e => setPhoneDraft(phoneDigits(e.target.value))}
                  style={{ flex: 1, fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38 }}
                />
                <Btn variant="success" onClick={phoneSave} disabled={phoneLoading || !isValidPhone(phoneDraft)}>
                  {phoneLoading ? '...' : (S().common.save || 'Сохранить')}
                </Btn>
                <Btn variant="ghost" onClick={phoneCancel}><X size={16} /></Btn>
              </div>
              {phoneDraft && (
                <div style={{ fontSize: 11, color: t.textMuted }}>
                  {formatPhone(phoneDraft)}
                </div>
              )}
            </div>
          )}

          {phoneErr && (
            <div style={{ fontSize: 12, color: t.accentRed, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={12} /> {phoneErr}
            </div>
          )}
          {phoneOk && (
            <div style={{ fontSize: 12, color: t.accentGreen, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={12} /> {phoneOk}
            </div>
          )}
        </div>
      )}

      {/* ── Смена пароля ── */}
      {/* Чанк F.1: обёрнут <div> -> <form> для убирания Chrome DOM warning. */}
      <form onSubmit={e => e.preventDefault()}>
        {/* Чанк F.2: hidden username для accessibility. */}
        <input type="text" name="username" autoComplete="username" defaultValue={globalUser?.login || globalUser?.email || user?.login || ""} readOnly style={{ display: "none" }} aria-hidden="true" />
        <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>
          {S().admin.changePassword}
        </label>

        {/* Текущий пароль показываем только если юзер начал вводить новый */}
        {pw.length > 0 && (
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Lock size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
            <input
              type="password"
              autoComplete="current-password"
              value={currentPw}
              onChange={e => { setCurrentPw(e.target.value); setPwError(''); }}
              placeholder={S().admin.currentPasswordPh || 'Текущий пароль'}
              style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text }}
            />
          </div>
        )}

        <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Lock size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
            <input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={e => setPw(stripCyrillic(e.target.value))}
              placeholder={S().admin.newPasswordPlaceholder || 'Новый пароль (минимум 8)'}
              style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text }}
            />
          </div>
          <Btn variant="success" onClick={savePassword} disabled={pwSaving || pw.length < 8 || !currentPw}>
            {pwSaving ? '...' : (S().common.save || 'Сохранить')}
          </Btn>
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
          {S().admin.passwordHint || 'Минимум 8 символов. После смены все другие сессии будут завершены.'}
        </div>
        {pw && pw.length > 0 && pw.length < 8 && (
          <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.passwordMinLength}</div>
        )}
        {pwError && (
          <div style={{ fontSize: 12, color: t.accentRed, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertCircle size={12} /> {pwError}
          </div>
        )}
        {pwSuccess && (
          <div style={{ fontSize: 12, color: t.accentGreen, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={12} /> {pwSuccess}
          </div>
        )}
      </form>
    </Card>
  );
}
